import { henSiteBrowserUrl } from '@highereducation/request-context';

/**
 * Same-origin BFF path for the lead conversion-init route. The generic
 * `linkout` action and the StudyMatch result card both build their redirect
 * target against this single path so the client and the BFF cannot drift.
 *
 * Trailing slash is intentional: hen-site runs `trailingSlash: true`, so the
 * unsuffixed path 308-redirects to the slash form. For the `linkout` GET that
 * the action opens synchronously on click, requesting the canonical slash URL
 * avoids the extra 308 hop (and, cross-origin, a second preflight) before the
 * BFF's own 302 to the partner linkout.
 */
// Trailing slash is required: hen-site runs with `trailingSlash: true`, so the
// slash-less path is 308-redirected to this canonical form. The query string is
// appended after the slash (`…/conversion-init/?withRedirect=…`), matching the
// canonical URL the server would otherwise redirect to.
export const CONVERSION_INIT_PATH =
  '/api/hen/v1/delivery-service/lead/conversion-init/';

/**
 * Identity + attribution subset the `GET …?withRedirect=true` BFF mode accepts.
 * `offeringId` and `tenantId` are required (CMS rejects a conversion without
 * them); the rest are optional attribution fields.
 */
export interface ConversionRedirectParams {
  offeringId?: string;
  tenantId?: string;
  correlationId?: string;
  trafficSource?: string;
  monetizationChannel?: string;
  experience?: string;
  /**
   * Flow type key (e.g. `direct-to-results`, `question-based`). Forwarded so
   * campaign-management's conversion-time budget match uses the same flow type
   * the offerings `/search` call did. Without it, a budget restricted to a
   * `scope:flowType` is excluded at conversion time and the BFF falls back to
   * the site root — even though the card rendered as monetizable.
   */
  flowType?: string;
  /**
   * Full CMS v2 conversion `trackingContext` object (identity + monetization
   * classification: `webContext` with `anonymousId` / `sessionId` /
   * `instanceId` / `page` / `userAgent`, plus `formatType` / `formatSubtype` /
   * `writeKey` / `tenantId`). Restores Mobius parity for the Linkout / HLF GET
   * redirect: a GET can't carry the POST body's `trackingContext`, so it is
   * serialized to URL-encoded JSON and reconstructed on the BFF (falling back to
   * the individual Cohesion IDs below, then to the unreliable `x-rvedu-*`
   * headers, only when this is absent). Omitted cleanly when unavailable,
   * unserializable, or larger than {@link MAX_TRACKING_CONTEXT_JSON_LENGTH}.
   */
  trackingContext?: Record<string, unknown>;
  /**
   * Cohesion anonymous visitor UUID (`window._Cohesion.anonymousId`). Carried
   * as a query param so the BFF can populate `trackingContext.webContext` for
   * CMS — the GET redirect path cannot use a POST body. Acts as a lightweight
   * fallback when the full {@link ConversionRedirectParams.trackingContext} is
   * absent or dropped (over cap / unserializable).
   */
  anonymousId?: string;
  /**
   * Cohesion session UUID (`window._Cohesion.sessionId`). Same rationale as
   * `anonymousId`.
   */
  sessionId?: string;
  /**
   * Cohesion page-load UUID (`window._Cohesion.instanceId`). Same rationale
   * as `anonymousId`.
   */
  instanceId?: string;
  /**
   * Optional ISO 3166-1 alpha-2 country code for conversion budget/geo
   * matching (for example `US`). When present, the BFF forwards it as
   * `conversion.location.country`.
   */
  country?: string;
  /**
   * Optional subdivision/state code paired with {@link country}
   * (for example `TX`). When present, the BFF forwards it as
   * `conversion.location.state`.
   */
  state?: string;
  /**
   * Base URL for the same-origin lead BFF routes (mirrors
   * `ApiConfig.leadApiBaseUrl`). Defaults to `''` so the browser talks to a
   * relative, same-origin path.
   */
  baseUrl?: string;
}

/**
 * Upper bound (characters of serialized JSON, pre-encoding) on the
 * `trackingContext` param. A generous cap that comfortably fits the identity +
 * classification payload while guarding against a pathological `page.url` /
 * referrer blowing the redirect URL past proxy/browser length limits. When the
 * serialized context exceeds this, the param is dropped and the redirect
 * degrades to the header-derived identity on the BFF rather than opening a tab
 * at a URL that a proxy might truncate or reject.
 */
export const MAX_TRACKING_CONTEXT_JSON_LENGTH = 6000;

/**
 * Serialize the `trackingContext` for the redirect query, or return `undefined`
 * when it is empty, unserializable, or over the length cap. Pure + defensive:
 * a `JSON.stringify` throw (e.g. a circular ref) never breaks URL building.
 */
function serializeTrackingContext(
  trackingContext: Record<string, unknown> | undefined,
): string | undefined {
  if (!trackingContext || Object.keys(trackingContext).length === 0) {
    return undefined;
  }
  try {
    const json = JSON.stringify(trackingContext);
    if (
      !json ||
      json === '{}' ||
      json.length > MAX_TRACKING_CONTEXT_JSON_LENGTH
    ) {
      return undefined;
    }
    return json;
  } catch {
    return undefined;
  }
}

/**
 * Build the same-origin BFF redirect URL for the generic `linkout` flow:
 * `…/conversion-init?withRedirect=true&offeringId=…&tenantId=…`. The BFF creates
 * the conversion server-side and 302s to the CMS-interpolated linkout URL.
 *
 * Returns `null` when `offeringId` or `tenantId` is missing/blank — the caller
 * (dispatcher handler or result card) warns + no-ops rather than opening a tab
 * that the BFF would only 400. Every param is URL-encoded via `URLSearchParams`.
 */
export function buildConversionRedirectUrl(
  params: ConversionRedirectParams,
): string | null {
  const offeringId = params.offeringId?.trim();
  const tenantId = params.tenantId?.trim();
  if (!offeringId || !tenantId) {
    return null;
  }

  const search = new URLSearchParams();
  search.set('withRedirect', 'true');
  search.set('offeringId', offeringId);
  search.set('tenantId', tenantId);
  if (params.correlationId) {
    search.set('correlationId', params.correlationId);
  }
  if (params.trafficSource) {
    search.set('trafficSource', params.trafficSource);
  }
  if (params.monetizationChannel) {
    search.set('monetizationChannel', params.monetizationChannel);
  }
  if (params.experience) {
    search.set('experience', params.experience);
  }
  if (params.flowType) {
    search.set('flowType', params.flowType);
  }
  const serializedTrackingContext = serializeTrackingContext(
    params.trackingContext,
  );
  if (serializedTrackingContext) {
    search.set('trackingContext', serializedTrackingContext);
  }
  const anonymousId = params.anonymousId?.trim();
  if (anonymousId) {
    search.set('anonymousId', anonymousId);
  }
  const sessionId = params.sessionId?.trim();
  if (sessionId) {
    search.set('sessionId', sessionId);
  }
  const instanceId = params.instanceId?.trim();
  if (instanceId) {
    search.set('instanceId', instanceId);
  }
  const country = params.country?.trim().toUpperCase();
  if (country) {
    search.set('country', country);
  }
  const state = params.state?.trim().toUpperCase();
  if (state) {
    search.set('state', state);
  }

  return `${henSiteBrowserUrl(params.baseUrl ?? '', CONVERSION_INIT_PATH)}?${search.toString()}`;
}
